import { addDays, differenceInDays, format, parseISO } from "date-fns";
import type { Schedule } from "../store/appStore";
import type { NoticeOfAssignment } from "../types/schema";
import type { PostDisbursementCase, PostDisbStatus, SupplierInvoiceSlot } from "../types/postDisbursement";

export const DUE_WINDOW_DAYS = 30;

export function computeCaseStatus(c: Pick<PostDisbursementCase, "completionPercent" | "dueDate" | "charge" | "ewayBills" | "taxInvoices" | "insurance" | "nach">): PostDisbStatus {
  const pct = c.completionPercent;
  const daysLeft = differenceInDays(parseISO(c.dueDate), new Date());
  if (pct >= 100) return "Complete";
  if (daysLeft < 0) return "Overdue";
  const touched =
    c.charge.status === "Filed" || c.charge.status === "NotApplicable" ||
    c.ewayBills.length > 0 ||
    c.taxInvoices.length > 0 ||
    c.insurance.length > 0 ||
    c.nach.status === "Active" || c.nach.status === "NotApplicable";
  return touched ? "In Progress" : "Not Started";
}

export type PiCompletionState = 'Pending' | 'TI Only' | 'EWB N/A' | 'Complete';

export function piCompletion(
  pi: { ref: string; ewbApplicability?: 'Pending' | 'Applicable' | 'NotApplicable' },
  ewayBills: Array<{ supplierInvoiceRef: string }>,
  taxInvoices: Array<{ supplierInvoiceRef: string }>
): PiCompletionState {
  const hasTi = taxInvoices.some(t => t.supplierInvoiceRef === pi.ref);
  const hasEwb = ewayBills.some(e => e.supplierInvoiceRef === pi.ref);
  const ewbNA = pi.ewbApplicability === 'NotApplicable';
  const ewbCovered = hasEwb || ewbNA;
  if (hasTi && ewbCovered) return ewbNA && !hasEwb ? 'EWB N/A' : 'Complete';
  if (hasTi) return 'TI Only';
  return 'Pending';
}

export function computeCompletion(
  c: Pick<PostDisbursementCase, "charge" | "ewayBills" | "taxInvoices" | "insurance" | "nach" | "supplierInvoices" | "assetIds" | "shipments">
): number {
  const assetCount = Math.max(c.assetIds.length, 1);
  const shipments = c.shipments || [];
  let tax = 0, ewb = 0;
  let countTax = true, countEwb = true;
  if (shipments.length > 0) {
    const shpTotal = shipments.reduce((s, sh) => s + (sh.totalAssetValue || 0), 0) || 1;
    const tiCov = shipments
      .filter(sh => sh.taxInvoiceFiles.length > 0)
      .reduce((s, sh) => s + (sh.totalAssetValue || 0), 0);
    const ewbCov = shipments
      .filter(sh => sh.ewbApplicability === 'NotApplicable' || sh.ewayBillFiles.length > 0)
      .reduce((s, sh) => s + (sh.totalAssetValue || 0), 0);
    tax = tiCov / shpTotal;
    ewb = ewbCov / shpTotal;
  } else {
    const piTotal = c.supplierInvoices.reduce((s, p) => s + (p.invoiceValue || 0), 0);
    const piTiCovered = c.supplierInvoices
      .filter(pi => c.taxInvoices.some(t => t.supplierInvoiceRef === pi.ref))
      .reduce((s, p) => s + (p.invoiceValue || 0), 0);
    const piEwbCovered = c.supplierInvoices
      .filter(pi =>
        pi.ewbApplicability === 'NotApplicable' ||
        c.ewayBills.some(e => e.supplierInvoiceRef === pi.ref)
      )
      .reduce((s, p) => s + (p.invoiceValue || 0), 0);
    tax = piTotal > 0 ? piTiCovered / piTotal : 0;
    ewb = piTotal > 0 ? piEwbCovered / piTotal : 0;
    if (piTotal === 0) { countTax = false; countEwb = false; }
  }
  // Charge & NACH: N/A excluded from denominator
  let chargeNum = 0, chargeDen = 1;
  if (c.charge.status === 'Filed') chargeNum = 1;
  else if (c.charge.status === 'NotApplicable') { chargeDen = 0; }
  let nachNum = 0, nachDen = 1;
  if (c.nach.status === 'Active') nachNum = 1;
  else if (c.nach.status === 'NotApplicable') { nachDen = 0; }
  const ins = Math.min(c.insurance.length, assetCount) / assetCount;

  let num = chargeNum + ins + nachNum;
  let den = chargeDen + 1 + nachDen;
  if (countTax) { num += tax; den += 1; }
  if (countEwb) { num += ewb; den += 1; }
  return den === 0 ? 0 : Math.round((num / den) * 100);
}

export function recomputeCase(c: PostDisbursementCase): PostDisbursementCase {
  const completionPercent = computeCompletion(c);
  const caseStatus = computeCaseStatus({ ...c, completionPercent });
  return { ...c, completionPercent, caseStatus, updatedAt: new Date().toISOString() };
}

import type { Shipment, ShipmentStatus } from "../types/postDisbursement";
export function shipmentStatus(s: Shipment): ShipmentStatus {
  const tiOk = s.taxInvoiceFiles.length > 0;
  const ewbOk = s.ewbApplicability === 'NotApplicable' || s.ewayBillFiles.length > 0;
  if (tiOk && ewbOk) return 'Uploaded';
  if (tiOk || ewbOk) return 'Partial';
  return 'Pending';
}

export function daysLeft(dueDateISO: string): number {
  return differenceInDays(parseISO(dueDateISO), new Date());
}

export function daysLeftBucket(d: number): { label: string; tone: "emerald" | "amber" | "red" | "destructive" } {
  if (d < 0) return { label: `${Math.abs(d)}d overdue`, tone: "destructive" };
  if (d <= 7) return { label: `${d}d left`, tone: "red" };
  if (d <= 14) return { label: `${d}d left`, tone: "amber" };
  return { label: `${d}d left`, tone: "emerald" };
}

// Build initial cases from existing schedules + NoAs that have a disbursement date.
export function buildInitialCases(schedules: Schedule[], noas: NoticeOfAssignment[]): PostDisbursementCase[] {
  const noaByRs = new Map<string, NoticeOfAssignment>();
  noas.forEach(n => { if (n.scheduleId) noaByRs.set(n.scheduleId, n); });

  const cases: PostDisbursementCase[] = [];
  schedules.forEach(s => {
    const noa = noaByRs.get(s.id);
    // Trigger: NoA has disbursement date OR schedule is approved (as fallback for demo data)
    const disbursed = noa?.disbursementDate || (s.status === "Approved" || s.status === "Active" ? s.config.commencementDate : undefined);
    if (!disbursed) return;

    // Group assets by poNumber → synthetic supplier invoice slots
    const groups = new Map<string, { vendor: string; assetIds: string[]; value: number }>();
    s.assets.forEach(a => {
      const key = a.poNumber || "PO-DEFAULT";
      const g = groups.get(key) || { vendor: a.vendorName || "Vendor", assetIds: [], value: 0 };
      g.assetIds.push(a.assetId);
      g.value += (a.basePrice || 0) * (a.quantity || 1);
      groups.set(key, g);
    });
    const supplierInvoices: SupplierInvoiceSlot[] = Array.from(groups.entries()).map(([ref, g]) => ({
      ref, vendor: g.vendor, assetIds: g.assetIds, invoiceValue: g.value
    }));

    const dueDate = format(addDays(parseISO(disbursed), DUE_WINDOW_DAYS), "yyyy-MM-dd");
    const base: PostDisbursementCase = {
      rsId: s.id,
      noaId: noa?.noaId,
      customerName: s.config.customer.name,
      lenderName: noa?.lender,
      disbursementDate: disbursed,
      disbursementAmount: noa?.disbursementAmount,
      dueDate,
      charge: noa?.chargeFilingDetails
        ? {
            status: noa.chargeFilingDetails.status === "Filed" ? "Filed" : "Pending",
            dueDate,
            filedOn: noa.chargeFilingDetails.filingDate,
          }
        : { status: "Pending", dueDate },
      ewayBills: [],
      taxInvoices: [],
      insurance: [],
      nach: { status: "Pending" },
      supplierInvoices,
      assetIds: s.assets.map(a => a.assetId),
      caseStatus: "Not Started",
      completionPercent: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      activityLog: [
        { ts: new Date().toISOString(), user: "System", action: `Case opened on disbursement (${format(parseISO(disbursed), "dd MMM yyyy")})` }
      ],
    };
    cases.push(recomputeCase(base));
  });
  return cases;
}
